/**
 * Xero bills — frontend API calls (P2P v1).
 *
 * The "new bills from Xero" queue: supplier bills that exist in Xero but have not yet been
 * recorded in MyGlobe. Everything here reads a local cache in the backend — no call reaches
 * Xero, and nothing here creates or changes an expenditure record.
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

const getAuthToken = () => localStorage.getItem("token");

async function request(path, { method = "GET", body } = {}) {
  const token = getAuthToken();
  const headers = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(body == null ? {} : { "Content-Type": "application/json" }),
  };

  const res = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers,
    ...(body != null ? { body: JSON.stringify(body) } : {}),
  });

  let data;
  const ct = res.headers.get("content-type");
  if (ct && ct.includes("application/json")) data = await res.json();
  else data = { success: res.ok, message: await res.text() };

  if (!res.ok) return { success: false, status: res.status, ...data };
  return data;
}

const qs = (params) => {
  const s = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") s.append(k, v);
  });
  const out = s.toString();
  return out ? `?${out}` : "";
};

/** Headline counts and the state of the last sync. */
export const getXeroBillsSummary = () => request("/api/xero-bills/summary");

/**
 * Bills not yet recorded in MyGlobe.
 * @param {{page,limit,search,supplier,from,to,sort,dir}} params
 */
export const getXeroBillQueue = (params) => request(`/api/xero-bills/queue${qs(params)}`);

/**
 * One bill, with property and job suggestions and a ready-made prefill object for the
 * expenditure form.
 */
export const getXeroBill = (xeroInvoiceId) =>
  request(`/api/xero-bills/${encodeURIComponent(xeroInvoiceId)}`);

/** Job suggestions for a property the user has chosen rather than the one we guessed. */
export const getXeroBillTickets = (xeroInvoiceId, propertyId) =>
  request(
    `/api/xero-bills/${encodeURIComponent(xeroInvoiceId)}/tickets${qs({ property_id: propertyId })}`
  );

/**
 * Create the expenditure record for a bill, from what the person confirmed on screen.
 * The amount, invoice number, supplier and dates all come from the cached Xero bill on the
 * server — only the decisions are sent from here.
 *
 * @param {{property_id, type_id, ticket_id, bank_account_id, description, purchase_order_number}} payload
 */
export const recordXeroBill = (xeroInvoiceId, payload) =>
  request(`/api/xero-bills/${encodeURIComponent(xeroInvoiceId)}/record`, {
    method: "POST",
    body: payload,
  });

/**
 * "That is not the same invoice" — clears an automatic match so the bill goes back to the
 * work list. Matching guesses; this is how a wrong guess gets corrected rather than leaving
 * a bill sitting against a record that is not it.
 */
export const unmatchXeroBill = (xeroInvoiceId) =>
  request(`/api/xero-bills/${encodeURIComponent(xeroInvoiceId)}/unmatch`, { method: "POST" });

/**
 * What Xero says about the payment of given expenditure records.
 * Bulk — ask once for the page being shown, not once per row.
 * Returns an object keyed by expenditure id.
 */
export const getPaymentStatus = (expenditureIds) => {
  const ids = (expenditureIds || []).filter(Boolean);
  if (!ids.length) return Promise.resolve({ success: true, data: {} });
  return request(`/api/xero-bills/payment-status${qs({ expenditure_ids: ids.join(",") })}`);
};

/** Where MyGlobe and Xero disagree — counts and values per category. */
export const getReconciliation = () => request("/api/xero-bills/reconciliation");

/** The rows behind one comparison category. */
export const getReconciliationCategory = (key, params) =>
  request(`/api/xero-bills/reconciliation/${encodeURIComponent(key)}${qs(params)}`);
